import React, { FC, ReactElement } from 'react';

import classNames from 'classnames';

import { IExerciseCardProps } from './exercise-card.interface';
import styles from './exercise-card.module.scss';

import { KnowledgeAreaLevelExercisesApi } from 'services';
import { extractLinksFromText } from 'utils';
import { ExerciseCategoryId } from 'views/components/Exercise/exercise.interface';
import { LinkPreviewCard } from 'views/components/LinkPreviewCard';
import {
  AttachmentLink,
  Bookmark,
  Card,
  Lightning,
  Headline3,
  Text,
} from 'views/components/UI';

const ExerciseCard: FC<IExerciseCardProps> = props => {
  const { exerciseData, actions, titleBadge, children, className } = props;
  const { name, description, exCategory, attachments } = exerciseData;
  const { segments } = extractLinksFromText(description);

  const isPractice = exCategory.id === ExerciseCategoryId.PRACTICE;
  const CategoryIcon = isPractice ? Lightning : Bookmark;

  const logoClassNames = classNames(styles.exercise__logo, {
    [styles['exercise__logo--practice']]: isPractice,
  });

  function renderDescriptionSegment(
    segment: (typeof segments)[number],
    index: number,
  ): ReactElement | null {
    if (segment.type === 'link') {
      return (
        <LinkPreviewCard
          key={`${segment.type}-${segment.url}-${index}`}
          url={segment.url}
        />
      );
    }

    if (!segment.content.trim()) {
      return null;
    }

    return (
      <div key={`${segment.type}-${index}`} className={styles.exercise__description}>
        <Text className={styles.exercise__descriptionText}>{segment.content}</Text>
      </div>
    );
  }

  return (
    <Card cardPadding="1.25rem" className={classNames(styles.exercise, className)}>
      <div className={styles.exercise__header}>
        <Headline3 color="var(--sk-text-default)" className={styles.exercise__title}>
          {name}
          {titleBadge && (
            <>
              {'\u00a0'}
              <span className={styles.exercise__titleBadge}>{titleBadge}</span>
            </>
          )}
        </Headline3>
        {actions && <div className={styles.exercise__actions}>{actions}</div>}
      </div>

      <div className={styles.exercise__category}>
        <figure className={logoClassNames}>
          <CategoryIcon size="16px" color="currentColor" />
        </figure>
        <Text className={styles.exercise__categoryLabel}>{exCategory.name}</Text>
      </div>

      <div className={styles.exercise__content}>
        {segments.map(renderDescriptionSegment)}
        {attachments && attachments.length > 0 && (
          <ul className={styles.exercise__attachments}>
            {attachments.map(attachment => (
              <li key={attachment.id}>
                <AttachmentLink
                  fileName={attachment.fileName}
                  contentType={attachment.contentType}
                  sizeBytes={attachment.sizeBytes}
                  requestDownloadUrl={() =>
                    new KnowledgeAreaLevelExercisesApi()
                      .getAttachmentDownloadUrl(attachment.id)
                      .then(response => response.downloadUrl)
                  }
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {children}
    </Card>
  );
};

export { ExerciseCard };
